import { useState, useEffect, useCallback } from 'react';
import { BlockedNumber, AddBlockedNumberRequest, BlockFromCallRequest } from '../types';
import { ApiClient } from '@utils/apiClient';
import { handleApiResult, handleApiCatch } from '@utils/apiErrorHandler';

export const useBlockedNumbers = () => {
  const [blockedNumbers, setBlockedNumbers] = useState<BlockedNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBlockedNumbers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await ApiClient.get('/telephony/blocked-numbers/');
      const result = await handleApiResult<BlockedNumber[]>(response, 'Failed to fetch blocked numbers');

      if (result.success && result.data) {
        setBlockedNumbers(result.data);
      } else {
        setError(result.errors?.generalError || 'Failed to fetch blocked numbers');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to fetch blocked numbers');
      setError(processedErrors.generalError || 'Failed to fetch blocked numbers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlockedNumbers();
  }, [fetchBlockedNumbers]);

  const addBlockedNumber = async (data: AddBlockedNumberRequest): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await ApiClient.post('/telephony/blocked-numbers/', data);
      const result = await handleApiResult<BlockedNumber>(response, 'Failed to block number');

      if (result.success && result.data) {
        setBlockedNumbers(prev => [result.data!, ...prev]);
        return { success: true };
      } else {
        return {
          success: false,
          error:
            result.errors?.fieldErrors?.number ||
            result.errors?.nonFieldErrors?.[0] ||
            result.errors?.generalError ||
            'Failed to block number',
        };
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to block number');
      return { success: false, error: processedErrors.generalError || 'Failed to block number' };
    }
  };

  const blockFromCall = async (data: BlockFromCallRequest): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await ApiClient.post('/telephony/blocked-numbers/from_call/', data);
      const result = await handleApiResult<BlockedNumber>(response, 'Failed to block number');

      if (result.success && result.data) {
        setBlockedNumbers(prev => [result.data!, ...prev]);
        return { success: true };
      } else {
        return {
          success: false,
          error:
            result.errors?.nonFieldErrors?.[0] ||
            result.errors?.generalError ||
            'Failed to block number',
        };
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to block number');
      return { success: false, error: processedErrors.generalError || 'Failed to block number' };
    }
  };

  const removeBlockedNumber = async (id: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await ApiClient.delete(`/telephony/blocked-numbers/${id}/`);
      if (response.status === 204) {
        setBlockedNumbers(prev => prev.filter(n => n.id !== id));
        return { success: true };
      } else {
        return { success: false, error: 'Failed to remove blocked number' };
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to remove blocked number');
      return { success: false, error: processedErrors.generalError || 'Failed to remove blocked number' };
    }
  };

  return {
    blockedNumbers,
    loading,
    error,
    fetchBlockedNumbers,
    addBlockedNumber,
    blockFromCall,
    removeBlockedNumber,
  };
};
